import type { JSXElementConstructor, ReactElement } from 'react';
import { cloneElement, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { MODAL_ID } from 'shared/lib/hooks';
import { getKeys } from 'shared/lib/object';
import { useUrlResolver } from 'shared/lib/url';
import { Skeleton } from 'shared/ui/Skeleton';
import type { Column, TableProps } from 'shared/ui/Table/table-models';

interface RenderRowsParams<T> extends TableProps<T> {
  getActiveRow: (id?: string | number) => string;
}

export function renderRows<T>({ data, rowClassName, rowKey, onRowClick, getActiveRow, columns }: RenderRowsParams<T>) {
  return (data ?? []).map((row) => {
    const className = `${rowClassName?.(row)} ${getActiveRow(row.id)}`;

    return (
      <tr
        key={rowKey(row)}
        {...(className ? { className } : {})}
        onClick={() => onRowClick?.(row)}
      >
        {columns.map(({ columnCell, columnLoadingCell, key }, index) => {
          return !row.loading
            ? cloneElement(
                columnCell({
                  value: row[key as keyof T],
                  row,
                }),
                { key }
              )
            : getLoadingCell(columnLoadingCell, index);
        })}
      </tr>
    );
  });
}

function getLoadingCell(columnLoadingCell: (() => ReactElement) | undefined, index: number) {
  return columnLoadingCell ? (
    cloneElement(columnLoadingCell(), { key: index })
  ) : (
    <td key={index}>
      <Skeleton className="skeleton h-[22px]" />
    </td>
  );
}

const SKELETON_ROWS_COUNT = 10;

export function renderLoadingRows<T>(columns: Column<T>[], rows = SKELETON_ROWS_COUNT) {
  return [...Array(rows)].map((_, index) => (
    <tr key={index}>{columns.map(({ columnLoadingCell }, index) => getLoadingCell(columnLoadingCell, index))}</tr>
  ));
}

interface SetStickyProps {
  node: HTMLDivElement;
  isAlwaysSticky?: boolean;
  offsetTop: number;
  offsetLeft?: number;
  setIsSticky: (isSticky: boolean) => void;
}

export function getRect(element: Element | null): DOMRect {
  if (element) {
    return element.getBoundingClientRect();
  }

  return {
    height: 0,
    width: 0,
    x: 0,
    y: 0,
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
    toJSON: () => {},
  };
}

export const setSticky = ({ node, offsetTop, setIsSticky, offsetLeft, isAlwaysSticky = false }: SetStickyProps) => {
  const [stickyHeaderWrapperNode, originalHeadersWrapperNode] = node.querySelectorAll('thead');
  const originalHeadersWrapperNodeRect = getRect(originalHeadersWrapperNode);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!stickyHeaderWrapperNode || !originalHeadersWrapperNode) {
    return;
  }

  const originalHeaders = originalHeadersWrapperNode.firstChild?.childNodes;
  const stickyHeaders = stickyHeaderWrapperNode.firstChild?.childNodes;

  if (!(originalHeaders?.[0] && stickyHeaders?.[0])) {
    return;
  }

  const nodeRect = getRect(node);

  stickyHeaderWrapperNode.style.width = `${nodeRect.width}px`;
  stickyHeaderWrapperNode.style.height = `${originalHeadersWrapperNodeRect.height}px`;

  stickyHeaderWrapperNode.style.left = `${offsetLeft ?? nodeRect.left}px`;
  stickyHeaderWrapperNode.style.top = `${offsetTop}px`;

  const offsetX = (originalHeaders[0] as HTMLElement).getBoundingClientRect().left;

  stickyHeaders.forEach((header, i) => {
    const originalElement = originalHeaders[i] as HTMLElement;
    const originalHeaderRect = getRect(originalElement);
    const stickyHeader = header as HTMLElement;

    stickyHeader.style.left = `${originalHeaderRect.left - offsetX - node.scrollLeft}px`;
    stickyHeader.style.minWidth = `${originalHeaderRect.width}px`;
    stickyHeader.style.maxWidth = `${originalHeaderRect.width}px`;

    if (offsetLeft) {
      stickyHeader.style.height = `${originalHeaderRect.height}px`;
      stickyHeader.style.lineHeight = `${originalHeaderRect.height}px`;
    }
  });
  const isSticky = originalHeadersWrapperNodeRect.top <= offsetTop || isAlwaysSticky;

  setIsSticky(isSticky);
};

export const getFilteredChildren = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  child: ReactElement<any, string | JSXElementConstructor<any>>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ReactElement<any, string | JSXElementConstructor<any>> => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!child || !child.props) {
    return child;
  }

  const { children, ...rest } = child.props;

  const propsList = getKeys(rest).filter((attr) => {
    if (typeof attr === 'string') {
      return attr.startsWith('data-autotest');
    }

    return false;
  });

  // Add '-sticky' postfix for sticky header elements
  propsList.forEach((attr) => {
    delete rest[attr];
    if (typeof attr === 'string') {
      rest[`${attr}-sticky`] = '';
    }
  });

  // Add '-sticky' postfix for src/shared/ui/Sortable.tsx component
  // if it is in sticky header
  if (rest.sortKey) {
    rest.dataAttributePostfix = 'sticky';
  }

  return { ...child, props: { children: getFilteredChildren(children), ...rest } };
};

export const useActiveRow = () => {
  const location = useLocation();
  const { getParam } = useUrlResolver();

  return useCallback(
    // intentionally overriding background class
    (id?: number | string) => (getParam(MODAL_ID) === `${id}` ? '!bg-deep-blue-50' : ''),
    [location.search]
  );
};
